import React, { useState, useEffect, useCallback } from 'react';
import { getCamerasData, getSettings } from '../api';
import CameraGrid from '../components/CameraGrid';
import moment from 'moment-timezone';
import './Dashboard.css';

const Dashboard = () => {
  const [cameras, setCameras] = useState([]);
  const [columns, setColumns] = useState(4);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [lastUpdateColor, setLastUpdateColor] = useState('');
  const [lastUpdateRaw, setLastUpdateRaw] = useState(null);
  const [objects, setObjects] = useState([]);
  const [activeObjectId, setActiveObjectId] = useState(null);
  const [activeObjectName, setActiveObjectName] = useState('');
  const [currentTimezone, setCurrentTimezone] = useState('Europe/Moscow');
  const [isSwitching, setIsSwitching] = useState(false);

  // Парсинг даты из пакета
  const parsePacketDate = useCallback((dateStr, timeStr, datetimeField, tz) => {
    // Если есть datetime_field, используем его
    if (datetimeField) {
      return moment(datetimeField).tz(tz).toDate();
    }
    
    // Если нет, парсим из date и time
    if (!dateStr || !timeStr) return null;
    
    const day = dateStr.substring(0, 2);
    const month = dateStr.substring(2, 4);
    const year = '20' + dateStr.substring(4, 6);
    const hours = timeStr.substring(0, 2);
    const minutes = timeStr.substring(2, 4);
    const seconds = timeStr.substring(4, 6);
    
    // Парсим как UTC, затем конвертируем в выбранную временную зону
    const utcDate = moment.utc(`${year}-${month}-${day} ${hours}:${minutes}:${seconds}`, 'YYYY-MM-DD HH:mm:ss');
    return utcDate.tz(tz).toDate();
  }, []);

  // Форматирование даты для отображения
  const formatLastUpdate = useCallback((dateObj) => {
    if (!dateObj) return 'Нет данных';
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    const seconds = String(dateObj.getSeconds()).padStart(2, '0');
    return `${day}.${month}.${year} в ${hours}:${minutes}:${seconds}`;
  }, []);

  // Определение цвета на основе разницы в минутах
  const getColorClass = useCallback((packetDate) => {
    if (!packetDate) return '';
    const now = Date.now();
    const diffMs = now - packetDate.getTime();
    const diffMinutes = diffMs / (1000 * 60);
    
    if (diffMinutes < 7) return 'recent';      // Зелёный - менее 7 минут
    if (diffMinutes < 14) return 'warning';    // Жёлтый - от 7 до 14 минут
    return 'expired';                           // Красный - более 14 минут
  }, []);

  // Переключение объекта
  const switchObject = async (objectId) => {
    if (isSwitching) return;
    setIsSwitching(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://eosmon.ru/api/settings/switch-object', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ objectId })
      });
      
      if (response.ok) {
        setActiveObjectId(objectId);
        const obj = objects.find(o => o.id === objectId);
        setActiveObjectName(obj?.name || '');
        // Принудительно обновляем данные после переключения
        await fetchData(true);
      } else {
        console.error('Ошибка переключения объекта:', await response.json());
      }
    } catch (error) {
      console.error('Ошибка переключения объекта:', error);
    } finally {
      setIsSwitching(false);
    }
  };

  // Загрузка данных
  const fetchData = useCallback(async (forceRefresh = false) => {
    try {
      const data = await getCamerasData();
      
      // Обновляем камеры
      setCameras(Array.isArray(data.cameras) ? data.cameras : []);
      setColumns(data.gridColumns || 4);
      
      // Обновляем список объектов
      if (data.objects && Array.isArray(data.objects)) {
        setObjects(data.objects);
        setActiveObjectId(data.activeObjectId);
        const activeObj = data.objects.find(o => o.id === data.activeObjectId);
        setActiveObjectName(activeObj?.name || (data.objects[0]?.name || ''));
      }
      
      // Обновляем временную зону активного объекта
      if (data.timezone) {
        setCurrentTimezone(data.timezone);
      }
      
      // Обновляем время последнего обновления
      if (data.lastUpdate) {
        const packetDate = parsePacketDate(
          data.lastUpdate.date,
          data.lastUpdate.time,
          data.lastUpdate.datetime,
          data.timezone || 'Europe/Moscow'
        );
        setLastUpdate(packetDate);
        setLastUpdateRaw(data.lastUpdate);
        setLastUpdateColor(getColorClass(packetDate));
      } else {
        setLastUpdate(null);
        setLastUpdateRaw(null);
        setLastUpdateColor('');
      }
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      setCameras([]);
    }
  }, [parsePacketDate, getColorClass]);

  // Автообновление каждые 3 секунды
  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(), 3000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Получение текстового описания цвета для времени
  const getTimeStatusText = (colorClass) => {
    switch (colorClass) {
      case 'recent': return 'Свежие данные';
      case 'warning': return 'Данные устаревают';
      case 'expired': return 'Данные устарели!';
      default: return '';
    }
  };

  // Вычисление разницы в минутах для отображения
  const getMinutesDiff = (packetDate) => {
    if (!packetDate) return null;
    const diffMs = Date.now() - packetDate.getTime();
    return Math.floor(diffMs / (1000 * 60));
  };

  const minutesDiff = getMinutesDiff(lastUpdate);

  return (
    <div className="dashboard">
      <header>
        <div className="header-left">
          <h1>EOS Monitor</h1>
          <p>Система мониторинга</p>
        </div>
        
        <div className="header-center">
          {objects.length > 1 && (
            <div className="object-selector">
              <label>Объект:</label>
              <select 
                value={activeObjectId || ''} 
                onChange={(e) => switchObject(e.target.value)}
                disabled={isSwitching}
              >
                {objects.map(obj => (
                  <option key={obj.id} value={obj.id}>
                    {obj.name} ({obj.imei || 'IMEI не задан'})
                  </option>
                ))}
              </select>
              {isSwitching && <span className="switching-indicator">⏳ Переключение...</span>}
            </div>
          )}
          {objects.length === 1 && (
            <div className="active-object-name">
              {activeObjectName}
            </div>
          )}
        </div>
        
        <div className="header-right">
          <span className="total-label">ВСЕГО КАМЕР</span>
          <span className="total-number">{cameras.length}</span>
        </div>
      </header>
      
      {/* Блок с информацией о времени последнего обновления */}
      <div className="last-update-container">
        {lastUpdate ? (
          <div className={`last-update-card ${lastUpdateColor}`}>
            <div className="last-update-icon">
              {lastUpdateColor === 'recent' && '🟢'}
              {lastUpdateColor === 'warning' && '🟡'}
              {lastUpdateColor === 'expired' && '🔴'}
            </div>
            <div className="last-update-info">
              <div className="last-update-label">Последние данные</div>
              <div className="last-update-time">{formatLastUpdate(lastUpdate)}</div>
              <div className="last-update-status">{getTimeStatusText(lastUpdateColor)}</div>
            </div>
            <div className="last-update-diff">
              {minutesDiff !== null && (
                <>
                  <div className="diff-value">{minutesDiff}</div>
                  <div className="diff-label">мин. назад</div>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="last-update-card no-data">
            <div className="last-update-icon">⚠️</div>
            <div className="last-update-info">
              <div className="last-update-label">Нет данных</div>
              <div className="last-update-time">Ожидание первого пакета...</div>
            </div>
          </div>
        )}
        
        {/* Дополнительная информация о временной зоне */}
        <div className="timezone-info">
          <span className="timezone-label">⏰ Временная зона:</span>
          <span className="timezone-value">{currentTimezone}</span>
        </div>
      </div>
      
      <CameraGrid cameras={cameras} columns={columns} />
    </div>
  );
};

export default Dashboard;